import React, {useEffect, useState} from "react";
import {Button, Chip, CircularProgress, Link, Sheet, Table, Typography} from "@mui/joy";
import Box from "@mui/joy/Box";
import IconButton from "@mui/joy/IconButton";
import {KeyboardArrowLeft, KeyboardArrowRight} from "@mui/icons-material";
import GeneralApiService from "../../axios/Axios";
import {toast} from "react-toastify";
import FranchiseFeePaymentModal from "../modals/FranchiseFeePaymentModal";
import ResponsiveTableWrapper from "../ui/ResponsiveTableWrapper";

interface FeeRow {
    id: number;
    period_start: string;
    period_end: string;
    amount: number;
    status: string;
    hosted_invoice_url: string;
    franchise_location?: string;
}

const statusColor = (status: string) => {
    if (status === 'paid') return 'success';
    if (status === 'open') return 'warning';
    return 'neutral';
};

const labelDisplayedRows = ({from, to, count}: { from: number; to: number; count: number; }) =>
    `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;

export default function FranchiseFeesTable() {
    const [fees, setFees] = useState<FeeRow[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(10);
    const [payingId, setPayingId] = useState<number | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const location = localStorage.getItem('location');

    const loadFees = async () => {
        setLoaded(false);
        try {
            const data = await GeneralApiService.getFranchiseFees();
            setFees(data || []);
        } catch (e) {
            toast.error("Could not load franchise fees.");
            setFees([]);
        } finally {
            setLoaded(true);
        }
    };

    useEffect(() => {
        loadFees();
    }, []);

    const handlePayClick = (id: number) => {
        setPayingId(id);
        setModalOpen(true);
    };

    const paginated = fees.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const pageCount = fees.length;
    const displayedTo = Math.min(pageCount, (page + 1) * rowsPerPage);

    const isAllContext = location === 'All';
    const colSpan = isAllContext ? 6 : 5;

    return (
        <ResponsiveTableWrapper minWidth={700}>
            <Table hoverRow stickyHeader aria-label="Franchise Fees">
                <thead>
                <tr>
                    {isAllContext && <th>Franchise</th>}
                    <th>Period</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Invoice</th>
                    <th style={{ width: 140 }}>Actions</th>
                </tr>
                </thead>
                <tbody>
                {!loaded ? (
                    <tr><td colSpan={colSpan} style={{ textAlign: 'center', padding: '2rem' }}><CircularProgress /></td></tr>
                ) : paginated.length === 0 ? (
                    <tr><td colSpan={colSpan} style={{ textAlign: 'center', padding: '1.5rem' }}>
                        <Typography level="body-sm" color="neutral">No franchise fees found.</Typography>
                    </td></tr>
                ) : (
                    paginated.map(fee => (
                        <tr key={fee.id}>
                            {isAllContext && <td>{fee.franchise_location}</td>}
                            <td>{fee.period_start} – {fee.period_end}</td>
                            <td>£{fee.amount.toFixed(2)}</td>
                            <td>
                                <Chip size="sm" color={statusColor(fee.status)} variant="soft">
                                    {fee.status || 'unknown'}
                                </Chip>
                            </td>
                            <td>
                                {fee.hosted_invoice_url ? (
                                    <Link href={fee.hosted_invoice_url} target="_blank" rel="noreferrer">
                                        View Invoice
                                    </Link>
                                ) : '—'}
                            </td>
                            <td>
                                {fee.status !== 'paid' && (
                                    <Button
                                        size="sm"
                                        variant="solid"
                                        color="success"
                                        loading={payingId === fee.id && modalOpen}
                                        onClick={() => handlePayClick(fee.id)}
                                    >
                                        Pay Now
                                    </Button>
                                )}
                            </td>
                        </tr>
                    ))
                )}
                </tbody>
                <tfoot>
                <tr>
                    <td colSpan={colSpan}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end', p: 1 }}>
                            <Typography textAlign="center" sx={{ minWidth: 80 }}>
                                {labelDisplayedRows({
                                    from: pageCount === 0 ? 0 : page * rowsPerPage + 1,
                                    to: displayedTo,
                                    count: pageCount,
                                })}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton size="sm" color="neutral" variant="outlined" disabled={page === 0}
                                            onClick={() => setPage(page - 1)} sx={{ bgcolor: 'background.surface' }}>
                                    <KeyboardArrowLeft />
                                </IconButton>
                                <IconButton size="sm" color="neutral" variant="outlined"
                                            disabled={page >= Math.ceil(pageCount / rowsPerPage) - 1}
                                            onClick={() => setPage(page + 1)} sx={{ bgcolor: 'background.surface' }}>
                                    <KeyboardArrowRight />
                                </IconButton>
                            </Box>
                        </Box>
                    </td>
                </tr>
                </tfoot>
            </Table>
            <FranchiseFeePaymentModal
                open={modalOpen}
                feeId={payingId}
                onClose={() => { setModalOpen(false); setPayingId(null); }}
                onPaid={async () => { await loadFees(); }}
            />
        </ResponsiveTableWrapper>
    );
}
